// skipcq: JS-W1028, JS-0128
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import { httpsCallable } from 'firebase/functions';

import { functions } from '../../utils/firebase';
import { useBreweryPortalSearch } from '../../hooks/useBreweryPortalSearch';
import { breweryPortalAutocompleteLabel } from '../../utils/breweryPortalOptionLabel';

const CREATE_BREWERY_GROUP_CALLABLE =
  'callableBreweryCreateBreweryGroup-createBreweryGroup';

function filterAutocompleteServerOptionsListedIdentity(optionListPassed) {
  return optionListPassed;
}

function BreweryAutocompleteField({
  labelText,
  inputValue,
  onInputChangeBridge,
  optionsList,
  isLoadingListed,
  errorMessageDescribe,
  onBreweryChosenDescribe,
}) {
  const trimmedValueInternal = inputValue.trim();
  const minCharsListed = 2;
  const needMoreCharsListed = trimmedValueInternal.length < minCharsListed;

  const handleInputCaptured = useCallback(
    (_ignoredEvent, nextTextCaptured, causeFlagDescribe) => {
      if (causeFlagDescribe === 'reset') return;
      onInputChangeBridge(nextTextCaptured ?? '');
    },
    [onInputChangeBridge],
  );

  const handleBrewerySelectionCaptured = useCallback(
    (eventConstructed, breweryOptionConstructed) => {
      if (typeof onBreweryChosenDescribe === 'function') {
        onBreweryChosenDescribe(eventConstructed, breweryOptionConstructed);
      }
    },
    [onBreweryChosenDescribe],
  );

  const renderTextFieldConstructed = useCallback(
    (paramsConstructed) => (
      <TextField
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...paramsConstructed}
        margin="dense"
        label={labelText}
        helperText={
          errorMessageDescribe
          || (
            needMoreCharsListed
              ? `Type at least ${minCharsListed} characters.`
              : 'Select a brewery from the list.'
          )
        }
        error={Boolean(errorMessageDescribe)}
      />
    ),
    [errorMessageDescribe, labelText, needMoreCharsListed],
  );

  return (
    <Autocomplete
      fullWidth
      options={optionsList}
      getOptionLabel={breweryPortalAutocompleteLabel}
      loading={isLoadingListed}
      value={null}
      inputValue={inputValue}
      filterOptions={filterAutocompleteServerOptionsListedIdentity}
      onInputChange={handleInputCaptured}
      onChange={handleBrewerySelectionCaptured}
      renderInput={renderTextFieldConstructed}
    />
  );
}

function CreationStripeRemoveRowConstructed({
  brewerMemberStripeConstructed,
  createBusyConstructed,
  chipRemovalConstructed,
}) {
  const handleStripeRemovalCommitted = useCallback(() => {
    chipRemovalConstructed(brewerMemberStripeConstructed.id);
  }, [brewerMemberStripeConstructed.id, chipRemovalConstructed]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
      }}
    >
      <Typography variant="body2">
        {breweryPortalAutocompleteLabel(brewerMemberStripeConstructed)}{' '}
        ({brewerMemberStripeConstructed.id})
      </Typography>
      <Button
        variant="text"
        size="small"
        disabled={createBusyConstructed}
        onClick={handleStripeRemovalCommitted}
      >
        Remove
      </Button>
    </Box>
  );
}

function dedupeMembersById(memberListPassed) {
  const mapPreserved = new Map();
  memberListPassed.forEach((memberItem) => {
    if (memberItem?.id && memberItem.data) {
      mapPreserved.set(memberItem.id, memberItem);
    }
  });
  return Array.from(mapPreserved.values());
}

/**
 * Dialog to create a brewery group (brand + ≥2 locations).
 * @param {object} root0
 * @param {boolean} root0.open
 * @param {() => void} root0.onClose
 * @param {Array<{ id: string, data: Record<string, unknown> }>} [root0.prefillMembers]
 * @param {() => void} [root0.onCreated]
 */
function CreateBreweryGroupDialog({
  open,
  onClose,
  prefillMembers,
  onCreated,
}) {
  const wasOpenRef = useRef(false);

  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createSearchInput, setCreateSearchInput] = useState('');
  const [debouncedCreateSearch, setDebouncedCreateSearch] = useState('');
  const [createSelectedMembers, setCreateSelectedMembers] = useState([]);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    const timerConstructed = window.setTimeout(() => {
      setDebouncedCreateSearch(createSearchInput);
    }, 280);
    return () => window.clearTimeout(timerConstructed);
  }, [createSearchInput]);

  const {
    breweries: createSearchHits,
    isLoading: createSearchLoading,
    error: createSearchError,
  } = useBreweryPortalSearch(debouncedCreateSearch);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setCreateError('');
      setCreateDisplayName('');
      setCreateSearchInput('');
      setDebouncedCreateSearch('');
      const seedList = Array.isArray(prefillMembers) && prefillMembers.length > 0
        ? dedupeMembersById(prefillMembers)
        : [];
      setCreateSelectedMembers(seedList);
    }
    wasOpenRef.current = open;
  }, [open, prefillMembers]);

  const createChosenIds = useMemo(
    () => new Set(
      createSelectedMembers.map((selectedMember) => selectedMember.id),
    ),
    [createSelectedMembers],
  );

  const addPickedMemberToCreationBuffer = useCallback(
    (_ignoredInputEvent, breweryOptionConstructed) => {
      if (!breweryOptionConstructed?.id) return;
      const nextIdConstructed = breweryOptionConstructed.id.trim();
      if (!nextIdConstructed) return;

      setCreateSelectedMembers((priorListConstructed) => {
        const mapPreservedConstructed = new Map(
          priorListConstructed.map(
            (priorMemberConstructed) =>
              [priorMemberConstructed.id, priorMemberConstructed],
          ),
        );
        mapPreservedConstructed.set(nextIdConstructed, breweryOptionConstructed);
        return Array.from(mapPreservedConstructed.values());
      });

      setCreateSearchInput('');
      setDebouncedCreateSearch('');
      setCreateError('');
    },
    [],
  );

  const chipRemoveFromCreation = useCallback((breweryIdRemoveBuilt) => {
    setCreateSelectedMembers((priorConstructed) =>
      priorConstructed.filter((itemIterate) => itemIterate.id !== breweryIdRemoveBuilt));
  }, []);

  const submitCreate = useCallback(async () => {
    const nameTrimConstructed = createDisplayName.trim();

    if (nameTrimConstructed.length === 0) {
      setCreateError('Display name is required.');
      return;
    }

    if (createSelectedMembers.length < 2) {
      setCreateError('Pick at least two breweries.');
      return;
    }

    const breweryIdsPayloadConstructed = createSelectedMembers.map(
      (memberIterate) => memberIterate.id.trim(),
    );

    const callableCreated = httpsCallable(
      functions,
      CREATE_BREWERY_GROUP_CALLABLE,
    );

    setCreateBusy(true);
    setCreateError('');

    try {
      await callableCreated({
        displayName: nameTrimConstructed,
        breweryIds: breweryIdsPayloadConstructed,
      });
      onClose();
      setCreateDisplayName('');
      setCreateSelectedMembers([]);
      setCreateSearchInput('');
      setDebouncedCreateSearch('');
      if (typeof onCreated === 'function') {
        onCreated();
      }
    } catch (submitFamilyError) {
      setCreateError(
        submitFamilyError?.message || 'Could not create group.',
      );
    } finally {
      setCreateBusy(false);
    }
  }, [createDisplayName, createSelectedMembers, onClose, onCreated]);

  const captureBrandTyping = useCallback((eventCaptured) => {
    setCreateDisplayName(eventCaptured.target.value);
  }, []);

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
      <DialogTitle>New brewery group</DialogTitle>
      <DialogContent dividers>
        {createError ? (
          <Typography color="error" sx={{ mb: 2 }}>
            {createError}
          </Typography>
        ) : null}

        <TextField
          fullWidth
          sx={{ mb: 2 }}
          margin="dense"
          label="Brand display name"
          value={createDisplayName}
          onChange={captureBrandTyping}
        />

        <BreweryAutocompleteField
          labelText="Add locations (national search)"
          inputValue={createSearchInput}
          errorMessageDescribe={createSearchError}
          optionsList={createSearchHits}
          onInputChangeBridge={setCreateSearchInput}
          isLoadingListed={createSearchLoading}
          onBreweryChosenDescribe={addPickedMemberToCreationBuffer}
        />

        <Box sx={{ mt: 2 }}>
          {createSelectedMembers.map((stripeMemberConstructed) => (
            <CreationStripeRemoveRowConstructed
              key={stripeMemberConstructed.id}
              brewerMemberStripeConstructed={stripeMemberConstructed}
              createBusyConstructed={createBusy}
              chipRemovalConstructed={chipRemoveFromCreation}
            />
          ))}
          <Typography sx={{ mt: 1 }} variant="caption" color="text.secondary">
            Selected: {createChosenIds.size} • need ≥ 2 distinct breweries
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={createBusy}
          onClick={submitCreate}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateBreweryGroupDialog;
